import { useEffect, useState } from "react"
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Radio,
  Stack,
  Typography,
} from "@mui/material"
import CardIcon from "@mui/icons-material/CreditCardOutlined"
import MailIcon from "@mui/icons-material/MailOutlineRounded"

import { billingApi } from "../../api"
import { money } from "./InvoiceTable"

/**
 * How this invoice should be collected.
 *
 * ── Why it is a question and not a setting ──────────────────────────────────
 *
 * Sending somebody a payment link and taking the money out of their account are
 * different acts, and which one is right is a fact about this invoice rather
 * than about the client: the same client is billed automatically every month and
 * sent a one-off for a project overrun they have not agreed to yet. A default
 * buried in their settings decides that on the operator's behalf, and the
 * operator finds out afterwards.
 *
 * ── Why the card is shown here ──────────────────────────────────────────────
 *
 * Choosing "charge automatically" for a client with no card on file produces an
 * invoice that sits open forever while everybody believes it was collected. The
 * server refuses it, but a refusal after pressing the button is a worse way to
 * learn this than the sentence next to the option.
 */
export default function IssueInvoiceDialog({ invoice, onClose, onIssued, onError }) {
  const [collection, setCollection] = useState("send_invoice")
  const [autopay, setAutopay] = useState(null)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    if (!invoice) return
    setCollection("send_invoice")
    setAutopay(null)
    billingApi
      .autopay({ client_id: invoice.client_id })
      .then(setAutopay)
      .catch(() => setAutopay({ authorized: false, card: null }))
  }, [invoice])

  const card = autopay?.card
  const canCharge = Boolean(autopay?.authorized && card)

  const submit = async () => {
    setBusy(true)
    try {
      const result = await billingApi.issue(invoice.id, collection)
      onIssued(result.message)
    } catch (err) {
      onError?.(err.userMessage)
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog open={Boolean(invoice)} onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Send {invoice?.number}
        <Typography variant="caption" color="text.secondary" display="block">
          {invoice ? `${money(invoice.total_cents, invoice.currency)} to ${invoice.client_label}` : ""}
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={1.5}>
          <Choice
            selected={collection === "send_invoice"}
            onSelect={() => setCollection("send_invoice")}
            icon={MailIcon}
            title="Ask them to pay"
            description="Stripe emails a payment link and the invoice waits. They choose when, and with what."
          />
          <Choice
            selected={collection === "charge_automatically"}
            onSelect={() => canCharge && setCollection("charge_automatically")}
            disabled={!canCharge}
            icon={CardIcon}
            title="Charge the card on file"
            description={
              canCharge
                ? `Taken from the ${card.brand} ending ${card.last4} as soon as this is finalized. Stripe retries by itself if it is declined.`
                : autopay === null
                  ? "Checking whether a card is on file…"
                  : `${invoice?.client_label || "This client"} has not authorised a card, so there is nothing to charge. Send them the link, or ask them to add one first.`
            }
          />

          {/* A card on file is not the same as permission for this invoice.
              Worth saying once, where the decision is being made. */}
          {collection === "charge_automatically" ? (
            <Alert severity="warning">
              This takes {money(invoice?.total_cents, invoice?.currency)} from {invoice?.client_label} straight away.
            </Alert>
          ) : null}
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={busy} color="inherit">
          Cancel
        </Button>
        <Button variant="contained" onClick={submit} disabled={busy}>
          {busy ? "Sending…" : collection === "charge_automatically" ? "Charge now" : "Send invoice"}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

/** One of the two, as a card rather than a radio in a list: the description is
 *  the part that decides it, so it has to be inside the target. */
function Choice({ selected, onSelect, disabled, icon: Icon, title, description }) {
  return (
    <Stack
      direction="row"
      spacing={1.5}
      alignItems="flex-start"
      role="radio"
      aria-checked={selected}
      aria-disabled={disabled || undefined}
      tabIndex={disabled ? -1 : 0}
      onClick={disabled ? undefined : onSelect}
      onKeyDown={(event) => {
        if (disabled || (event.key !== "Enter" && event.key !== " ")) return
        event.preventDefault()
        onSelect()
      }}
      sx={(theme) => ({
        p: 1.5,
        borderRadius: 2,
        border: 1,
        borderColor: selected ? "primary.dark" : "divider",
        bgcolor: selected ? (theme.palette.mode === "dark" ? "rgba(126,211,72,0.12)" : "rgba(126,211,72,0.10)") : "transparent",
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.55 : 1,
        transition: theme.transitions.create(["border-color", "background-color"]),
        "&:focus-visible": { outline: `2px solid ${theme.palette.primary.dark}`, outlineOffset: 2 },
      })}
    >
      <Radio checked={selected} disabled={disabled} size="small" sx={{ p: 0, mt: 0.25 }} tabIndex={-1} />
      <Icon fontSize="small" sx={{ mt: 0.25, color: selected ? "primary.dark" : "text.secondary" }} />
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="body2" fontWeight={650}>
          {title}
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block">
          {description}
        </Typography>
      </Box>
    </Stack>
  )
}
